import { type FC, memo, type PropsWithChildren, useCallback, useEffect, useRef } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { BottomSheet, Popover, PopoverRole, usePopover } from "@stubhub/broadway-web";
const bottomSheetContent = css({
  maxHeight: "[84dvh]",
  overflowY: "auto",
});

const popoverContent = css({
  maxHeight: "[472px]",
  overflowY: "auto",
});

const popoverAnchor = css({
  position: "absolute",
  width: "[0]",
  height: "[0]",
  overflowX: "hidden",
  overflowY: "hidden",
});

interface Props {
  onClose: () => void;
}

export const SheetOrPopper: FC<PropsWithChildren<Props>> = memo(({ onClose, children }) => {
  "use no memo";
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const popover = usePopover();

  // Open on mount — SheetOrPopper is only rendered when it should be visible
  const { handleOpenChange: setPopoverOpen } = popover;
  const hasOpenedRef = useRef(false);
  useEffect(() => {
    // Guard is load-bearing: re-opening on any later run would fight the user closing the popover.
    if (hasOpenedRef.current) return;
    hasOpenedRef.current = true;
    setPopoverOpen(true);
  }, [setPopoverOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
      popover.handleOpenChange(open);
    },
    [onClose, popover],
  );

  if (!isDesktop) {
    return (
      <BottomSheet isOpen={popover.isOpen} onOpenChange={handleOpenChange}>
        <div className={bottomSheetContent}>{children}</div>
      </BottomSheet>
    );
  }

  return (
    <>
      {/* Zero-size anchor span so floating-ui has a reference point near where SheetOrPopper is rendered */}
      <span
        ref={popover.triggerProps["ref"] as React.RefCallback<HTMLSpanElement> | null}
        className={popoverAnchor}
        aria-hidden
      />
      <Popover
        role={PopoverRole.Menu}
        isOpen={popover.isOpen}
        isMounted={popover.isMounted}
        openModality={popover.openModality}
        animationState={popover.animationState}
        floatingProps={popover.floatingProps}
        floatingContext={popover.floatingContext}
        referenceElement={popover.referenceElement}
        handleOpenChange={handleOpenChange}
        direction={popover.direction}
        anchor="bottom"
      >
        <div className={popoverContent}>{children}</div>
      </Popover>
    </>
  );
});
SheetOrPopper.displayName = "SheetOrPopper";
